<!--
  @component CutoverIO — P4 effecting lens (u6 zero-downtime cutover).
  chain:  ctx.deployment.dispatch("cutover.*", …) → this → ctx.cutover
  effects: cross-arc cutover waves + pre-cutover backup + backup-prune.
           Distinct from ReleaseIO.cutover (release-arc-specific).
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { CUTOVER_IO_KEY } from "./deployment-io.js";
  import type { CutoverIOContext } from "./deployment-io.js";
  export { CUTOVER_IO_KEY };
  export type { CutoverIOContext };
  export function useCutoverIO(): CutoverIOContext {
    const ctx = getContext<CutoverIOContext | undefined>(CUTOVER_IO_KEY);
    if (!ctx) throw new Error("useCutoverIO() called outside of a CutoverIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { DeployTargetRef } from "./deployment-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: CutoverIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("cutover.")); },
    cutover: (target: DeployTargetRef, options?) => dispatchOp("cutover.run", { target, options }),
    backupBefore: (target) => dispatchOp("cutover.backup", { target }),
    backupPrune: (target, options?) => dispatchOp("cutover.backupPrune", { target, options }),
  } satisfies CutoverIOContext;

  setContext(CUTOVER_IO_KEY, ctx);
</script>
{@render children?.()}
